// Copyright (c) 2022-2024. Sendanor <info@sendanor.fi>. All rights reserved.

import { every } from "../io/hyperify/core/functions/every";
import { some } from "../io/hyperify/core/functions/some";
import { trim } from "../io/hyperify/core/functions/trim";
import { LogService } from "../io/hyperify/core/LogService";
import {
    Observer,
    ObserverCallback,
    ObserverDestructor,
} from "../io/hyperify/core/Observer";
import { isNumber } from "../io/hyperify/core/types/Number";
import { isString } from "../io/hyperify/core/types/String";
import { GEOIP_BACKEND_URL } from "../constants/backend";
import { SimpleHttpService } from "./SimpleHttpService";

const LOG = LogService.createLogger('GeoIpService');

function isOptionallyString (value : any) : value is (undefined | string) {
    return value === undefined || isString(value);
}

function isOptionallyNumber (value : any) : value is (undefined | number) {
    return value === undefined || isNumber(value);
}

function isArrayOfNumbers (value : any) : value is Array<Number> {
    return every(value, isNumber);
}

function isOptionallyArrayOfNumbers (value : any) : value is (undefined | Array<Number>) {
    return value === undefined || isArrayOfNumbers(value);
}

export interface GeoIpLookupParamsDTO {
    readonly ip ?: string;
}


export function isGeoIpLookupParamsDTO (value : any) : value is GeoIpLookupParamsDTO {

    return !!value && isString(value?.ip);

}

export function isOptionallyGeoIpLookupParamsDTO (value : any) : value is (undefined | GeoIpLookupParamsDTO) {
    return value === undefined || isGeoIpLookupParamsDTO(value);
}


export interface GeoIpLookupPayloadDTO {

    readonly area     ?: number;
    readonly city     ?: string;
    readonly country  ?: string;
    readonly eu       ?: string;
    readonly ll       ?: Array<number>;
    readonly metro    ?: number;
    readonly range    ?: Array<number>;
    readonly region   ?: string;
    readonly timezone ?: string;

}

export function isGeoIpLookupPayloadDTO (value : any) : value is GeoIpLookupPayloadDTO {

    return (
        !!value
        && isOptionallyNumber(value?.area)
        && isOptionallyString(value?.city)
        && isOptionallyString(value?.country)
        && isOptionallyString(value?.eu)
        && isOptionallyArrayOfNumbers(value?.ll)
        && isOptionallyNumber(value?.metro)
        && isOptionallyArrayOfNumbers(value?.range)
        && isOptionallyString(value?.region)
        && isOptionallyString(value?.timezone)
    );

}

export function isOptionallyGeoIpLookupPayloadDTO (value : any) : value is (undefined | GeoIpLookupPayloadDTO) {
    return value === undefined || isGeoIpLookupPayloadDTO(value);
}

export interface GeoIpLookupDTO {

    readonly acceptLanguage ?: string;
    readonly params         ?: GeoIpLookupParamsDTO;
    readonly payload        ?: GeoIpLookupPayloadDTO;

}

export function isOptionallyGeoIpLookupDTO (value : any) : value is GeoIpLookupDTO {
    return value === undefined || isGeoIpLookupDTO(value);
}

export function isGeoIpLookupDTO (value : any) : value is GeoIpLookupDTO {
    return (
        !!value
        && isOptionallyString(value?.acceptLanguage)
        && isOptionallyGeoIpLookupParamsDTO(value?.params)
        && isOptionallyGeoIpLookupPayloadDTO(value?.payload)
    );
}

export enum GeoIpServiceEvent {
    GEO_IP_DATA_UPDATED = "GeoIpService:dataUpdated"
}

export class GeoIpService {

    public static Event = GeoIpServiceEvent;

    private static _loadingGeoIpData : boolean = false;
    private static _geoIpData        : GeoIpLookupDTO | undefined;
    private static _observer         : Observer<GeoIpServiceEvent> = new Observer<GeoIpServiceEvent>("GeoIpService");

    public static initialize () {
        this.updateGeoIpData();
    }

    public static on (
        name : GeoIpServiceEvent,
        callback : ObserverCallback<GeoIpServiceEvent>
    ) : ObserverDestructor {
        return this._observer.listenEvent(name, callback);
    }

    public static isLoadingGeoIpData () : boolean {
        return this._loadingGeoIpData;
    }

    public static hasGeoIpData () : boolean {
        return this._geoIpData !== undefined;
    }

    public static getGeoIpData () : GeoIpLookupDTO | undefined {
        return this._geoIpData;
    }

    public static updateGeoIpData () {

        if (this._loadingGeoIpData) {
            LOG.warn('Already started to load geoIpData');
            return;
        }

        this._loadingGeoIpData = true;

        SimpleHttpService.getJson(GEOIP_BACKEND_URL).then((dto : any) => {

            this._loadingGeoIpData = false;

            if (!isGeoIpLookupDTO(dto)) {
                LOG.error('updateGeoIpData: invalid dto = ', dto);
                return Promise.reject('Invalid response from backend');
            }

            LOG.info('updateGeoIpData: Received: ', dto);

            this._geoIpData = dto;

            this._observer.triggerEvent(GeoIpServiceEvent.GEO_IP_DATA_UPDATED);

        }).catch(err => {
            this._loadingGeoIpData = false;
            LOG.error('Error: ', err);
        });

    }

    public static isFinnishLanguage () : boolean {

        const acceptLanguages = (this._geoIpData?.acceptLanguage ?? '').split(',');

        LOG.debug('isFinnishLanguage: acceptLanguages: ', acceptLanguages);

        return some(acceptLanguages, (item : string) => {
            item = trim(item);
            return item === 'fi' || item.startsWith('fi;');
        });

    }

    public static isFromFinland () : boolean {
        const country : string = trim(this._geoIpData?.payload?.country ?? '');
        LOG.debug('isFromFinland: country: ', country);
        return country.toUpperCase() === 'FI';
    }

}
